import { FormControl, FormHelperText } from '@mui/material';
import React, { FC, useState } from 'react';
import { connect } from 'react-redux';

import {
  MESSAGE__MAX_TEXT_LENGTH,
  MESSAGE__TEXT_REQUIRED,
} from '../../../constants/forms';
import useIsMobile from '../../../hooks/useIsMobile';
import translate from '../../../i18n/translate';

import { getUsage, getValidityProps } from './helpers';
import { mapDispatchToProps, mapStateToProps } from './redux';
import * as Styled from './styled';
import { Props } from './types';

/**
 * Container for message text
 *
 * @param props                   Props passed to the container
 * @param props.nodeId            Whether Editor is in template edit mode
 * @param props.nodeType          Whether the compose form is being used for messages or templates
 * @param props.requestTextUpdate Request that the text is updated in Redux
 * @param props.shouldValidate    Whether the input should be validated
 * @param props.text              Current draft text
 * @returns                       The container itself
 */
const ComposeMetaDescriptionContainer: FC<Props> = ({
  nodeId,
  nodeType,
  requestTextUpdate,
  shouldValidate = true,
  text,
}) => {
  const isMobile = useIsMobile();
  const [value, setValue] = useState('');

  /**
   * The user has changed the input value, so we have to track it to check validity
   *
   * @param val Value of textarea
   */
  const onChange = (val: string) => {
    setValue(val);
    requestTextUpdate(val);
  };

  const { error, helperText } = getValidityProps(
    MESSAGE__TEXT_REQUIRED === true,
    value,
    MESSAGE__MAX_TEXT_LENGTH,
  );

  const isInvalid = shouldValidate && error;

  return (
    <FormControl
      error={isInvalid}
      fullWidth={true}
      required={MESSAGE__TEXT_REQUIRED}
    >
      <Styled.StyledLabel shrink={true}>
        {translate('GENERAL__DESCRIPTION')}
      </Styled.StyledLabel>
      <Styled.Editor
        breakpointMin={null}
        isInvalid={isInvalid}
        isRequired={MESSAGE__TEXT_REQUIRED}
        maxLength={MESSAGE__MAX_TEXT_LENGTH}
        nodeId={nodeId}
        onChangeText={onChange}
        placeholder={translate('DESCRIPTION__WRITE')}
        usage={getUsage(isMobile, nodeType)}
        value={text}
      />
      {isInvalid && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ComposeMetaDescriptionContainer);
